"use client";
import { FormEvent, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Search,
  SlidersHorizontal,
  X,
  ArrowRight,
  Building2,
  MapPin,
} from "lucide-react";
import {
  DiscoverResults,
  SearchFilters,
  filterLabels,
  normalizeFilters,
  searchHref,
} from "@/lib/discover";
import ListingCards from "../listing-cards";
import styles from "../discover.module.css";

export default function SearchPageClient({
  results,
  filters,
}: {
  results: DiscoverResults;
  filters: SearchFilters;
}) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const booleanKeys = Object.keys(filterLabels) as Array<
    keyof typeof filterLabels
  >;
  const active = [
    ...(["query", "industry", "location"] as const)
      .filter((key) => filters[key])
      .map((key) => ({
        key,
        label: `${key === "query" ? "Search" : key === "industry" ? "Industry" : "Location"}: ${filters[key]}`,
      })),
    ...booleanKeys
      .filter((key) => filters[key])
      .map((key) => ({ key, label: filterLabels[key] })),
  ];
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const entries = Object.fromEntries(
      new FormData(event.currentTarget).entries(),
    ) as Record<string, string>;
    startTransition(() => router.push(searchHref(normalizeFilters(entries))));
  };
  const pages = Array.from(
    new Set([
      1,
      filters.page - 1,
      filters.page,
      filters.page + 1,
      results.totalPages,
    ]),
  )
    .filter((page) => page > 0 && page <= results.totalPages)
    .sort((a, b) => a - b);
  return (
    <main className={styles.page}>
      <section className={styles.searchHeroSection}>
        <div className={styles.container}>
          <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
            <Link href="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link href="/discover">Discover</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Search businesses</span>
          </nav>
          <section className={`${styles.hero} ${styles.searchHero}`}>
            <p className={styles.heroEyebrow}>
              <span />
              The Eyam business directory
            </p>
            <h1>
              {filters.industry
                ? `${filters.industry} businesses`
                : filters.query
                  ? `Results for “${filters.query}”`
                  : "Find your next connection."}
            </h1>
            <p className={styles.intro}>
              Find a service, meet a supplier, or discover a local business.
              Search by what you need, then narrow it down.
            </p>
            <form
              action="/discover/search"
              method="get"
              role="search"
              onSubmit={submit}
              key={searchHref(filters)}
            >
              <div className={styles.searchPanel}>
                <div className={styles.searchBar}>
                  <label className={styles.field} htmlFor="business-query">
                    What are you looking for?
                    <span className={styles.inputWrap}>
                      <Search size={19} aria-hidden="true" />
                      <input
                        className={styles.input}
                        id="business-query"
                        name="query"
                        type="search"
                        maxLength={200}
                        defaultValue={filters.query}
                        placeholder="Business name, service, or keyword"
                      />
                    </span>
                  </label>
                  <label className={styles.field} htmlFor="business-location">
                    Where?
                    <span className={styles.inputWrap}>
                      <MapPin size={19} aria-hidden="true" />
                      <input
                        id="business-location"
                        className={styles.input}
                        name="location"
                        maxLength={200}
                        defaultValue={filters.location}
                        placeholder="City, province, or country"
                      />
                    </span>
                  </label>
                  <button
                    className={styles.button}
                    type="submit"
                    disabled={pending}
                  >
                    <Search size={18} aria-hidden="true" />
                    {pending ? "Searching…" : "Search businesses"}
                  </button>
                </div>
              </div>
              <details
                className={styles.filters}
                open={Boolean(
                  filters.industry ||
                  filters.location ||
                  booleanKeys.some((key) => filters[key]) ||
                  filters.sort !== "relevance",
                )}
              >
                <summary>
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  More filters{active.length > 0 ? ` (${active.length})` : ""}
                </summary>
                <div className={styles.filterGrid}>
                  <label className={styles.field} htmlFor="business-industry">
                    Industry
                    <select
                      id="business-industry"
                      name="industry"
                      className={styles.input}
                      defaultValue={filters.industry}
                    >
                      <option value="">All industries</option>
                      {filters.industry &&
                        !results.industries.some(
                          (item) => item.name === filters.industry,
                        ) && <option>{filters.industry}</option>}
                      {results.industries.map((item) => (
                        <option key={item.name} value={item.name}>
                          {item.name} ({item.count} listed)
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.field} htmlFor="business-sort">
                    Sort results
                    <select
                      id="business-sort"
                      name="sort"
                      className={styles.input}
                      defaultValue={filters.sort}
                    >
                      <option value="relevance">Best match</option>
                      <option value="newest">Recently added</option>
                      <option value="name-asc">Name: A–Z</option>
                      <option value="name-desc">Name: Z–A</option>
                    </select>
                  </label>
                </div>
                <fieldset className={styles.checks}>
                  <legend>
                    Only show businesses with all selected options
                  </legend>
                  {booleanKeys.map((key) => (
                    <label className={styles.check} key={key}>
                      <input
                        name={key}
                        type="checkbox"
                        value="true"
                        defaultChecked={filters[key]}
                      />
                      {filterLabels[key]}
                    </label>
                  ))}
                </fieldset>
                <div className={styles.chips}>
                  <button
                    type="submit"
                    className={styles.button}
                    disabled={pending}
                  >
                    Apply filters
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                  <Link href="/discover/search" className={styles.textLink}>
                    Reset search
                  </Link>
                </div>
              </details>
            </form>
          </section>
        </div>
      </section>
      <section className={styles.searchResultsSection}>
        <div className={styles.container}>
          {active.length > 0 && (
            <div className={styles.chips} aria-label="Active filters">
              {active.map(({ key, label }) => (
                <Link
                  key={key}
                  href={searchHref({ ...filters, [key]: undefined, page: 1 })}
                  className={styles.chip}
                  aria-label={`Remove ${label}`}
                >
                  {label}
                  <X size={13} aria-hidden="true" />
                </Link>
              ))}
              <Link className={styles.textLink} href="/discover/search">
                Clear all
              </Link>
            </div>
          )}
          <section aria-label="Search results" aria-busy={pending}>
            {results.isFuzzyMatch && (
              <p className={`${styles.notice} mt-6`} role="status">
                No exact matches for “{filters.query}”. Here are businesses with
                close spellings. Your filters are still applied.
              </p>
            )}
            <div className={styles.toolbar}>
              <div>
                <p className={styles.eyebrow}>Discover your options</p>
                <h2 role="status" aria-live="polite">
                  {pending
                    ? "Updating results…"
                    : `${results.total.toLocaleString()} ${results.total === 1 ? "business" : "businesses"} found`}
                </h2>
                {results.total > 0 && (
                  <p className={styles.muted}>
                    Showing {(filters.page - 1) * filters.pageSize + 1}–
                    {Math.min(filters.page * filters.pageSize, results.total)}
                  </p>
                )}
              </div>
              <Link href="/discover/create" className={styles.secondary}>
                List your business <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
            {results.items.length ? (
              <ListingCards items={results.items} />
            ) : (
              <div className={styles.empty}>
                <Building2 size={40} className="mx-auto" aria-hidden="true" />
                <h2>No businesses match just yet</h2>
                <p className={styles.muted}>
                  Try a shorter keyword, a nearby city, or remove a filter to
                  see more businesses.
                </p>
                <Link className={styles.button} href="/discover/search">
                  Explore all businesses
                </Link>
              </div>
            )}
            {results.totalPages > 1 && (
              <nav
                className={styles.pagination}
                aria-label="Search result pages"
              >
                {filters.page > 1 && (
                  <Link
                    href={searchHref({ ...filters, page: filters.page - 1 })}
                    rel="prev"
                  >
                    Previous
                  </Link>
                )}
                {pages.map((page, index) => (
                  <span key={page} style={{ display: "contents" }}>
                    {index > 0 && page - pages[index - 1] > 1 && (
                      <span aria-hidden="true">…</span>
                    )}
                    <Link
                      href={searchHref({ ...filters, page })}
                      aria-label={`Page ${page}`}
                      aria-current={page === filters.page ? "page" : undefined}
                    >
                      {page}
                    </Link>
                  </span>
                ))}
                {filters.page < results.totalPages && (
                  <Link
                    href={searchHref({ ...filters, page: filters.page + 1 })}
                    rel="next"
                  >
                    Next
                  </Link>
                )}
              </nav>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}
